import { useEffect, useMemo, useRef, useState } from "react";
import type { Vehicle } from "@/lib/api";
import { gpsDriveGet } from "@/lib/api/gpsdrive-client";

export type AnimatedVehicle = Vehicle & { bearing: number };

type LatLng = [number, number];

type Anim = {
  // Trajeto do deslize atual: começa onde o marcador estava e termina em
  // `rest` — pela rua (map-matching) ou, sem encaixe, a reta até o fix bruto.
  path: RoadPath;
  startTs: number;
  // Duração deste deslize e instante do último fix real recebido — a duração
  // acompanha o intervalo REAL entre fixes, pra o marcador andar sem parar
  // até o próximo chegar.
  durMs: number;
  fixTs: number;
  // Último fix REAL recebido (dedupe) e os últimos fixes brutos do veículo
  // (entrada do map-matching, do mais antigo pro mais novo).
  fixLat: number;
  fixLng: number;
  history: LatLng[];
  // Onde o marcador fica depois do deslize (fix encaixado na rua, ou o
  // bruto) e pra onde aponta. Rumo parado = direção da rua no último trecho
  // (o course do rastreador parado/devagar é ruído).
  rest: LatLng;
  restBearing: number | undefined;
  // Rumo mostrado quando o deslize começou — a rotação parte dele.
  startBearing: number | undefined;
  // Objeto devolvido pro veículo parado em `rest` (referência estável
  // enquanto `v` não muda — ver comentário no useMemo abaixo).
  restFor?: Vehicle;
  restObj?: AnimatedVehicle;
};

type RoadPath = { pts: LatLng[]; cum: number[]; total: number };

// Duração padrão do deslize do primeiro fix; nos fixes seguintes a duração é
// o intervalo medido desde o fix anterior (ver MIN/MAX_GLIDE_MS), em
// velocidade constante, pra o marcador não parar entre um fix e outro.
const GLIDE_MS = 4000;
// Deslize contínuo: cada trecho dura o intervalo medido desde o fix anterior
// (limitado a esta faixa), em velocidade constante, chegando ao destino
// quando o próximo fix deve chegar.
const MIN_GLIDE_MS = 2000;
const MAX_GLIDE_MS = 20000;

// Encaixe na rua (pedidos 2026-09-28: "a moto está andando por cima das
// casas" / "tem que andar certinho na estrada e quando virar, virar
// certinho"). Medido numa trilha real: os fixes ficam 20–50 m FORA da via
// (reflexo nos prédios), então nem o ponto parado nem a reta entre dois
// fixes ficavam na rua. O backend (GET /api/v1/map/road-path, map-matching
// OSRM com cache e limite de taxa) recebe os últimos fixes do veículo e
// devolve o último projetado na rua + o trajeto pela via. Só pra quem está
// visível (activeIds); qualquer falha/demora = reta até o fix bruto, como
// antes.
const MATCH_HISTORY = 5;
const MATCH_MIN_M = 5; // abaixo disso é ruído de GPS parado: não mexe
const MATCH_MAX_M = 3000;
const MATCH_WAIT_MS = 2500;
// Rotação: perto de cada esquina o rumo é misturado entre o trecho que
// chega e o que sai ao longo de TURN_M metros — o ícone gira durante a
// curva em vez de trocar de direção de uma vez.
const TURN_M = 10;

function distM(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const kx = 111320 * Math.cos((aLat * Math.PI) / 180);
  return Math.hypot((bLat - aLat) * 110540, (bLng - aLng) * kx);
}

function bearingDeg(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const kx = Math.cos((aLat * Math.PI) / 180);
  const deg = (Math.atan2((bLng - aLng) * kx, bLat - aLat) * 180) / Math.PI;
  return (deg + 360) % 360;
}

// Mistura dois rumos pelo menor ângulo (350° → 10° passa por 0°, não 180°).
function blendBearing(a: number, b: number, f: number): number {
  const diff = ((b - a + 540) % 360) - 180;
  return (a + diff * Math.min(1, Math.max(0, f)) + 360) % 360;
}

function buildRoadPath(raw: LatLng[]): RoadPath {
  const pts: LatLng[] = [];
  for (const p of raw) {
    const last = pts[pts.length - 1];
    if (!last || distM(last[0], last[1], p[0], p[1]) > 0.3) pts.push(p);
  }
  if (pts.length === 1) pts.push(pts[0]);
  const cum = [0];
  for (let i = 1; i < pts.length; i++) {
    cum.push(cum[i - 1] + distM(pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1]));
  }
  return { pts, cum, total: cum[cum.length - 1] };
}

function segBearing(p: RoadPath, i: number): number | undefined {
  const a = p.pts[i - 1];
  const b = p.pts[i];
  return p.cum[i] - p.cum[i - 1] > 0.5 ? bearingDeg(a[0], a[1], b[0], b[1]) : undefined;
}

function endBearing(p: RoadPath): number | undefined {
  for (let i = p.pts.length - 1; i >= 1; i--) {
    const b = segBearing(p, i);
    if (b !== undefined) return b;
  }
  return undefined;
}

type MatchResult = { snapped: LatLng; path: LatLng[] };

// No máximo MATCH_MAX_INFLIGHT pedidos de encaixe abertos ao mesmo tempo;
// passou disso, o veículo desliza em reta até o fix (o mesmo fallback de
// falha/demora). Relato 2026-10-05 ("quando fico muito tempo com a tela
// aberta começa a bugar, as imagens somem, Editar/Alertas demoram"): com a
// frota inteira visível eram ~1.500 road-path a cada 10 min por navegador
// (o MATCH_WAIT_MS só larga a espera, o pedido continua ocupando a
// conexão), enchendo as conexões com api.gpsdrive.com.br e deixando foto,
// Editar dispositivo e Alertas na fila atrás deles.
const MATCH_MAX_INFLIGHT = 3;
let matchInflight = 0;

async function fetchMatch(history: LatLng[]): Promise<MatchResult | undefined> {
  if (matchInflight >= MATCH_MAX_INFLIGHT) return undefined;
  const pts = history.map(([lat, lng]) => `${lat.toFixed(6)},${lng.toFixed(6)}`).join(";");
  const timeout = new Promise<undefined>((r) => setTimeout(() => r(undefined), MATCH_WAIT_MS));
  matchInflight++;
  const req = gpsDriveGet<{ ok: boolean; snapped: LatLng | null; path: LatLng[] }>(
    `/api/v1/map/road-path?pts=${encodeURIComponent(pts)}`,
  )
    .then((r) => (r.ok && r.snapped ? { snapped: r.snapped, path: r.path } : undefined))
    .catch(() => undefined)
    .finally(() => {
      matchInflight--;
    });
  return Promise.race([req, timeout]);
}

function posAt(a: Anim, now: number): { lat: number; lng: number; bearing: number | undefined } {
  const t = Math.min(1, Math.max(0, (now - a.startTs) / a.durMs));
  const p = a.path;
  if (t >= 1 || p.total <= 0) return { lat: a.rest[0], lng: a.rest[1], bearing: a.restBearing ?? a.startBearing };
  // Velocidade constante ao longo do trajeto.
  const d = t * p.total;
  let i = 1;
  while (i < p.cum.length - 1 && p.cum[i] < d) i++;
  const [aLat, aLng] = p.pts[i - 1];
  const [bLat, bLng] = p.pts[i];
  const seg = p.cum[i] - p.cum[i - 1];
  const f = seg > 0 ? (d - p.cum[i - 1]) / seg : 1;

  let bearing = segBearing(p, i) ?? a.restBearing ?? a.startBearing;
  if (bearing !== undefined) {
    const into = d - p.cum[i - 1]; // metros desde o início do trecho
    const left = p.cum[i] - d; // metros até o fim do trecho
    const half = Math.min(TURN_M, seg / 2);
    if (i === 1 && a.startBearing !== undefined && into < half) {
      // Saída: gira do rumo em que estava pro rumo do primeiro trecho.
      bearing = blendBearing(a.startBearing, bearing, into / Math.max(half, 0.01));
    } else if (i > 1 && into < half) {
      const prevB = segBearing(p, i - 1);
      if (prevB !== undefined) bearing = blendBearing(prevB, bearing, 0.5 + (0.5 * into) / Math.max(half, 0.01));
    }
    if (i < p.pts.length - 1 && left < half) {
      const nextB = segBearing(p, i + 1);
      if (nextB !== undefined) bearing = blendBearing(bearing, nextB, 0.5 - (0.5 * left) / Math.max(half, 0.01));
    }
  }
  return { lat: aLat + (bLat - aLat) * f, lng: aLng + (bLng - aLng) * f, bearing };
}

// Marcadores usam a posição real reportada pelo Traccar (GET /api/positions).
// Nenhuma posição é inventada: ao chegar um novo fix, o marcador desliza da
// posição anterior até a nova ao longo de GLIDE_MS em vez de saltar
// instantaneamente, sempre interpolando entre dois pontos reais.
//
// Fica ligado pra frota inteira, mesmo grande — a mitigação de performance
// pra frota grande é o corte por viewport em MapView (só quem tem marcador
// desenhado na tela entra no useEffect caro de rotação), não desligar o
// glide (achado no teste de carga de 5000 veículos, 2026-09-02: desligar a
// animação "resolvia" o travamento mas o produto queria o deslizamento
// preservado de verdade).
//
// `activeIds` (opcional): quais veículos valem a pena forçar um re-render
// (setTick) pra suavizar a animação. TODOS os veículos continuam
// interpolando de verdade — posAt() sempre calcula a posição real de
// qualquer um a partir do tempo decorrido, não é isso que muda. O que muda é
// só QUANDO o React re-renderiza por causa da animação: sem isso, com uma
// frota de milhares, quase sempre existe algum veículo fora de tela ainda
// deslizando, então o loop de rAF forçava ~20 re-renders/s do MapView pra
// sempre — cada um recalculando center/filtros/arrays sobre a frota inteira
// à toa, pra uma animação que não tinha marcador na tela pra ser vista de
// qualquer forma. Passar o Set de veículos dentro da área visível faz o tick
// só disparar quando há algo animando que o usuário realmente vê; fora
// disso, o veículo fora de tela recebe a posição correta normalmente na
// próxima vez que o componente renderizar por outro motivo (novo poll,
// pan/zoom). Achado no teste de carga de 5000 veículos, 2026-09-02.
export function useMovingVehicles(
  base: Vehicle[] | undefined,
  activeIds?: Set<string> | null,
): AnimatedVehicle[] {
  const animsRef = useRef<Map<string, Anim>>(new Map());
  const rafRef = useRef<number | null>(null);
  const activeIdsRef = useRef<Set<string> | null | undefined>(activeIds);
  activeIdsRef.current = activeIds;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!base) return;
    const anims = animsRef.current;
    const now = performance.now();
    const baseIds = new Set(base.map((v) => v.id));
    const active = activeIdsRef.current;
    for (const v of base) {
      const prev = anims.get(v.id);
      if (!prev) {
        const here: LatLng = [v.lat, v.lng];
        anims.set(v.id, {
          path: buildRoadPath([here]),
          startTs: now - GLIDE_MS,
          durMs: GLIDE_MS,
          fixTs: now,
          fixLat: v.lat,
          fixLng: v.lng,
          history: [here],
          rest: here,
          restBearing: v.bearing,
          startBearing: v.bearing,
        });
        continue;
      }
      if (prev.fixLat === v.lat && prev.fixLng === v.lng) continue;
      const fix: LatLng = [v.lat, v.lng];
      const history = [...prev.history, fix].slice(-MATCH_HISTORY);
      const moved = distM(prev.fixLat, prev.fixLng, v.lat, v.lng);
      if (moved < MATCH_MIN_M) {
        // Ruído de GPS parado: registra o fix mas não mexe o marcador.
        anims.set(v.id, { ...prev, fixLat: v.lat, fixLng: v.lng, history });
        continue;
      }
      // Parte de onde o marcador está agora (pode estar no meio de um glide
      // anterior), evitando um salto ao trocar de destino.
      const cur = posAt(prev, now);
      const here: LatLng = [cur.lat, cur.lng];
      const durMs = Math.min(MAX_GLIDE_MS, Math.max(MIN_GLIDE_MS, now - prev.fixTs));
      const straightPath = buildRoadPath([here, fix]);
      const straight: Anim = {
        path: straightPath,
        startTs: now,
        durMs,
        fixTs: now,
        fixLat: v.lat,
        fixLng: v.lng,
        history,
        rest: fix,
        restBearing: endBearing(straightPath) ?? v.bearing,
        startBearing: cur.bearing,
      };
      const visible = !active || active.has(v.id);
      if (!visible || moved > MATCH_MAX_M || history.length < 2) {
        anims.set(v.id, straight);
        continue;
      }
      // Segura o marcador onde está enquanto o encaixe chega (normalmente
      // < 1 s) e então desliza pela rua no tempo que sobrou.
      anims.set(v.id, { ...straight, path: buildRoadPath([here]), rest: here, restBearing: cur.bearing });
      const id = v.id;
      void fetchMatch(history).then((m) => {
        const a = animsRef.current.get(id);
        // Chegou outro fix nesse meio tempo: esta resposta ficou velha.
        if (!a || a.fixTs !== now) return;
        const t = performance.now();
        const remaining = Math.max(MIN_GLIDE_MS, durMs - (t - now));
        if (!m) {
          animsRef.current.set(id, { ...straight, startTs: t, durMs: remaining });
          return;
        }
        // Trajeto pela rua (do penúltimo ao último fix encaixados); sem ele,
        // reta curta até o ponto encaixado. Começa onde o marcador está.
        const road = buildRoadPath([here, ...(m.path.length ? m.path : []), m.snapped]);
        animsRef.current.set(id, {
          ...straight,
          path: road,
          startTs: t,
          durMs: remaining,
          rest: m.snapped,
          restBearing: endBearing(road) ?? v.bearing,
        });
      });
    }
    for (const id of anims.keys()) {
      if (!baseIds.has(id)) anims.delete(id);
    }
  }, [base]);

  useEffect(() => {
    let lastRender = 0;
    const step = (ts: number) => {
      const anims = animsRef.current;
      const active = activeIdsRef.current;
      let animating = false;
      for (const [id, a] of anims) {
        if (active && !active.has(id)) continue;
        if (ts - a.startTs < a.durMs) {
          animating = true;
          break;
        }
      }
      if (animating && ts - lastRender > 50) {
        lastRender = ts;
        setTick((t) => (t + 1) % 1_000_000);
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Memoizado em [base, tick]: sem isso, esta função rodava (map + spread
  // sobre a frota INTEIRA) em toda renderização do componente que chama o
  // hook, não só quando a posição de fato mudava — incluindo, por exemplo,
  // o setRevealTick de MapView que dispara a cada 60ms durante a rampa de
  // revelação de marcadores (ver REVEAL_BATCH_SIZE). Numa frota de 5000,
  // isso significava um rebuild completo do array (+ o filtro por viewport
  // que depende dele) a cada um desses ticks — ~330 vezes ao longo dos ~20s
  // de rampa só de entrar no mapa — sendo o gargalo real por trás do
  // travamento ainda presente mesmo após os outros cortes por viewport/lote
  // (achado no teste de carga de 5000 veículos, 2026-09-02). `tick` só avança
  // quando há alguma animação ativa em `activeIds` (ver loop de rAF acima),
  // então este memo continua atualizando a posição deslizante em tempo real
  // — só para de recalcular a frota inteira à toa em renders não relacionados.
  return useMemo(() => {
    if (!base) return [];
    const now = performance.now();
    return base.map((v) => {
      const anim = animsRef.current.get(v.id);
      // Sem glide em andamento (nunca animou, ou já terminou o GLIDE_MS):
      // devolve a MESMA referência sempre que possível, só normalizando
      // bearing quando falta. Antes, todo veículo era espalhado (`{...v}`)
      // incondicionalmente aqui, mesmo parado — como este useMemo roda de
      // novo sempre que `base` muda (ex.: 1 veículo atualizado via
      // WebSocket, ver use-realtime-sync.ts), isso recriava um objeto NOVO
      // pra frota INTEIRA a cada atualização de tempo real, mesmo com o
      // cache do React Query já preservando a referência de quem não mudou
      // — anulava o ganho do patch incremental bem na próxima etapa do
      // pipeline (VehicleMarker em routes/index.tsx não conseguia pular
      // ninguém). Achado ao corrigir o gargalo de tempo real de 5000
      // veículos, 2026-09-02.
      if (!anim) {
        return v.bearing !== undefined ? (v as AnimatedVehicle) : { ...v, bearing: 0 };
      }
      if (now - anim.startTs >= anim.durMs) {
        // Parado: no ponto encaixado na rua, apontando pra direção da via.
        // Mesmo objeto enquanto `v` não muda (ver comentário acima).
        const [lat, lng] = anim.rest;
        const bearing = anim.restBearing ?? v.bearing ?? 0;
        if (lat === v.lat && lng === v.lng && bearing === v.bearing) return v as AnimatedVehicle;
        if (anim.restFor !== v || !anim.restObj) {
          anim.restFor = v;
          anim.restObj = { ...v, lat, lng, bearing };
        }
        return anim.restObj;
      }
      const { lat, lng, bearing } = posAt(anim, now);
      return { ...v, lat, lng, bearing: bearing ?? v.bearing ?? 0 };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, tick]);
}
